<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
    <div class="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
      <!-- Header -->
      <div class="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <h2 class="text-xl font-bold text-white flex items-center">
          <svg class="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
          </svg>
          {{ $t('eds.title', 'Подтверждение по ЭЦП') }}
        </h2>
        <p class="text-blue-100 text-sm mt-1">
          {{ $t('eds.subtitle', 'Двухфакторная аутентификация') }}
        </p>
      </div>

      <!-- Content -->
      <div class="p-6">
        <!-- Step 1: Checking NCALayer -->
        <div v-if="step === 'checking'" class="text-center py-8">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p class="text-gray-600">{{ $t('eds.checkingNCALayer', 'Проверка NCALayer...') }}</p>
        </div>

        <!-- Step 2: NCALayer Not Running -->
        <div v-else-if="step === 'ncalayer-error'" class="text-center py-4">
          <div class="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-800 mb-2">
            {{ $t('eds.ncalayerNotRunning', 'NCALayer не запущен') }}
          </h3>
          <p class="text-gray-600 text-sm mb-4">
            {{ $t('eds.ncalayerInstruction', 'Для аутентификации по ЭЦП необходимо запустить программу NCALayer') }}
          </p>
          <div class="space-y-3">
            <a href="https://pki.gov.kz/ncalayer/" target="_blank" 
               class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
              </svg>
              {{ $t('eds.downloadNCALayer', 'Скачать NCALayer') }}
            </a>
            <button @click="checkNCALayer" 
                    class="block w-full px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md">
              {{ $t('eds.retry', 'Повторить проверку') }}
            </button>
          </div>
        </div>

        <!-- Step 3: Select Storage -->
        <div v-else-if="step === 'select-storage'" class="py-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t('eds.selectStorage', 'Выберите носитель ключа') }}
          </label>
          <select v-model="selectedStorage" 
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500">
            <option v-for="token in tokens" :key="token" :value="token">
              {{ token === 'PKCS12' ? $t('eds.fileStorage', 'Файл (*.p12)') : token }}
            </option>
          </select>
          <p class="text-sm text-gray-500 mt-2">
            {{ $t('eds.storageHint', 'PKCS12 - для файлов ЭЦП (.p12), или выберите аппаратный токен') }}
          </p>
          
          <button @click="signChallenge" 
                  :disabled="!selectedStorage || loading"
                  class="w-full mt-4 px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center">
            <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ loading ? $t('eds.signing', 'Подписание...') : $t('eds.selectKey', 'Выбрать ключ') }}
          </button>
        </div>

        <!-- Step 4: Signing in progress -->
        <div v-else-if="step === 'signing'" class="text-center py-8">
          <div class="animate-pulse">
            <svg class="w-16 h-16 text-blue-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"/>
            </svg>
          </div>
          <p class="text-gray-600">{{ $t('eds.waitingForSignature', 'Ожидание подписи в NCALayer...') }}</p>
          <p class="text-sm text-gray-500 mt-2">
            {{ $t('eds.enterPassword', 'Введите пароль от ЭЦП в открывшемся окне') }}
          </p>
        </div>

        <!-- Step 5: Success -->
        <div v-else-if="step === 'success'" class="text-center py-8">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-green-800 mb-2">
            {{ $t('eds.success', 'Личность подтверждена') }}
          </h3>
          <p class="text-gray-600 text-sm">
            {{ $t('eds.redirecting', 'Перенаправление...') }}
          </p>
        </div>

        <!-- Error message -->
        <div v-if="error" class="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p class="text-red-600 text-sm">{{ error }}</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 bg-gray-50 border-t flex justify-end">
        <button @click="cancel" 
                class="px-4 py-2 text-gray-600 hover:text-gray-800">
          {{ $t('common.cancel', 'Отмена') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ncaLayerService } from '@/services/ncaLayerService'
import api from '@/services/api'

export default {
  name: 'EDSAuthModal',
  
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['success', 'cancel', 'skip'],
  
  data() {
    return {
      step: 'checking', // checking, ncalayer-error, select-storage, signing, success
      tokens: [],
      selectedStorage: 'PKCS12',
      challenge: null,
      loading: false,
      error: null
    }
  },
  
  watch: {
    show(newVal) {
      if (newVal) {
        this.reset()
        this.checkNCALayer()
      }
    }
  },
  
  methods: {
    reset() {
      this.step = 'checking'
      this.tokens = []
      this.selectedStorage = 'PKCS12'
      this.challenge = null
      this.loading = false
      this.error = null
    },
    
    async checkNCALayer() {
      this.step = 'checking'
      this.error = null
      
      try {
        const isRunning = await ncaLayerService.isNCALayerRunning()
        
        if (!isRunning) {
          this.step = 'ncalayer-error'
          return
        }
        
        // Получаем список доступных токенов
        this.tokens = await ncaLayerService.getActiveTokens()
        
        // Получаем challenge с сервера
        const response = await api.getEDSChallenge()
        this.challenge = response.data.challenge
        
        this.step = 'select-storage'
        
      } catch (error) {
        console.error('NCALayer check error:', error)
        this.step = 'ncalayer-error'
        this.error = error.message || this.$t('eds.connectionError', 'Ошибка подключения к NCALayer')
      }
    },
    
    async signChallenge() {
      if (!this.challenge) {
        this.error = this.$t('eds.noChallenge', 'Challenge не получен. Попробуйте снова.')
        return
      }
      
      this.loading = true
      this.error = null
      this.step = 'signing'
      
      try {
        // Подписываем challenge через NCALayer
        const signature = await ncaLayerService.createCMSSignature(
          this.selectedStorage,
          this.challenge,
          true // include data
        )
        
        // Отправляем подпись на сервер для верификации
        const response = await api.verifyEDS(signature)
        
        if (response.data.success) {
          this.step = 'success'
          
          // Ждём немного и эмитим событие успеха
          setTimeout(() => {
            this.$emit('success', response.data)
          }, 1500)
        } else {
          throw new Error(response.data.error || this.$t('eds.verificationFailed', 'Ошибка верификации'))
        }
        
      } catch (error) {
        console.error('EDS signing error:', error)
        this.step = 'select-storage'
        this.error = error.response?.data?.error || error.message || this.$t('eds.signingError', 'Ошибка подписания')
      } finally {
        this.loading = false
      }
    },
    
    cancel() {
      ncaLayerService.disconnect()
      this.$emit('cancel')
    },
    
    skip() {
      ncaLayerService.disconnect()
      this.$emit('skip')
    }
  },
  
  beforeUnmount() {
    ncaLayerService.disconnect()
  }
}
</script>
